import { ReactElement, ReactNode, cloneElement, isValidElement } from 'react';
import { canPerform, roleAllowsAny } from './permissions';
import { ProjectAction, ProjectRole } from './types';

type RoleGateProps = {
  children: ReactNode;
  /** The current user's effective role for the active project (see useProjectRole). */
  projectRole: ProjectRole | null;
  /** Show children only when the role can perform this action. */
  action?: ProjectAction;
  /** Alternatively, show children only when the role is one of these. */
  allow?: ReadonlyArray<ProjectRole>;
  /**
   * When true, render the child but pass `disabled` instead of hiding it.
   * The child element must accept a `disabled` prop for this to work.
   */
  disableInsteadOfHide?: boolean;
  /** Rendered when the role does not allow the action. */
  fallback?: ReactNode;
};

export function RoleGate({
  children,
  projectRole,
  action,
  allow,
  disableInsteadOfHide = false,
  fallback = null,
}: RoleGateProps) {
  let allowed = false;
  if (action) {
    allowed = canPerform(projectRole, action);
  } else if (allow) {
    allowed = roleAllowsAny(projectRole, allow);
  }

  if (allowed) return <>{children}</>;

  if (
    disableInsteadOfHide &&
    isValidElement(children) &&
    typeof children.type !== 'string'
  ) {
    return cloneElement(children as ReactElement<{ disabled?: boolean }>, {
      disabled: true,
    });
  }

  return <>{fallback}</>;
}
